import { type FC, lazy, Suspense, useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from "react"
import TextAreaAutoSize from "react-textarea-autosize"
import styled, { css } from "styled-components"

import { type LexicalEditorType } from "@forento/ui/components/LexicalEditor"

import { exportLazyEditorState, isLazyEditorStateEmpty, loadLazyEditorState } from "~/utilities/lazyLexical"
import { componentBackgroundColor } from "~/utilities/styles"

import InputLabel from "./InputLabel"

const LexicalEditor = lazy(() => import("@forento/ui/components/LexicalEditor"))

interface InputFieldProps {
	label?: string
	value: string
	onChange?: (value: string) => void
	onFocus?: () => void
	placeholder?: string
	inputType?: "text" | "number" | "email" | "password" | "datetime-local" | "url"
	autoComplete?: "given-name" | "family-name" | "email" | "current-password" | "new-password" | "off"
	disabled?: boolean
	compact?: boolean
	required?: boolean
	inline?: boolean
	className?: string
	maxCharacters?: number
}

interface InputTextAreaProps {
	label?: string
	/** Names the field for assistive technology when no visible label is shown. */
	ariaLabel?: string
	value: string
	onChange: (value: string) => void
	placeholder?: string
	compact?: boolean
	inline?: boolean
	className?: string
	maxCharacters?: number
}

const InputField: FC<InputFieldProps> = ({
	label,
	value,
	onChange,
	onFocus,
	placeholder,
	inputType = "text",
	autoComplete,
	disabled,
	compact,
	required,
	inline,
	className,
	maxCharacters,
}) => {
	const inputId = useId()
	const handleChange = (value: string) => {
		onChange?.(maxCharacters === undefined ? value : value.slice(0, maxCharacters))
	}

	return (
		<Container className={className} $inline={inline ?? false}>
			{label !== undefined && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
			<Input
				id={inputId}
				type={inputType}
				value={value}
				onChange={e => handleChange(e.target.value)}
				onFocus={onFocus}
				placeholder={placeholder}
				autoComplete={autoComplete}
				disabled={disabled}
				maxLength={maxCharacters}
				$isCompact={compact ?? false}
				$inline={inline ?? false}
				data-sentry-mask={inputType === "password" ? true : undefined}
				required={required}
			/>
			{maxCharacters !== undefined && <CharacterCounter value={value} maxCharacters={maxCharacters} />}
		</Container>
	)
}

export const InputTextArea: FC<InputTextAreaProps> = ({
	label,
	ariaLabel,
	value,
	onChange,
	placeholder,
	compact,
	inline,
	className,
	maxCharacters,
}) => {
	const inputId = useId()
	const handleChange = (value: string) => {
		onChange(maxCharacters === undefined ? value : value.slice(0, maxCharacters))
	}

	return (
		<Container className={className} $inline={inline ?? false}>
			{label !== undefined && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
			<TextArea
				id={inputId}
				aria-label={ariaLabel}
				value={value}
				onChange={e => handleChange(e.target.value)}
				placeholder={placeholder}
				maxLength={maxCharacters}
				$isCompact={compact ?? false}
				$inline={inline ?? false}
			/>
			{maxCharacters !== undefined && <CharacterCounter value={value} maxCharacters={maxCharacters} />}
		</Container>
	)
}

export const DynamicInputTextArea: FC<InputTextAreaProps> = ({
	label,
	value,
	onChange,
	placeholder,
	compact,
	inline,
	className,
	maxCharacters,
}) => {
	const inputId = useId()
	const handleChange = (value: string) => {
		onChange(maxCharacters === undefined ? value : value.slice(0, maxCharacters))
	}

	return (
		<Container className={className} $inline={inline ?? false}>
			{label !== undefined && <InputLabel htmlFor={inputId}>{label}</InputLabel>}
			<DynamicTextArea
				id={inputId}
				value={value}
				onChange={e => handleChange(e.target.value)}
				placeholder={placeholder}
				maxLength={maxCharacters}
				$isCompact={compact ?? false}
				$inline={inline ?? false}
			/>
			{maxCharacters !== undefined && <CharacterCounter value={value} maxCharacters={maxCharacters} />}
		</Container>
	)
}

const CharacterCounter: FC<{ value: string; maxCharacters: number }> = ({ value, maxCharacters }) => {
	const ratio = maxCharacters === 0 ? 0 : value.length / maxCharacters
	const isAtLimit = maxCharacters > 0 && value.length >= maxCharacters

	return (
		<Counter $isAtLimit={isAtLimit} $ratio={ratio}>
			{value.length}/{maxCharacters}
		</Counter>
	)
}

type RichTextAreaParams = {
	label?: string
	extendedFormatting?: boolean
	isCompact?: boolean
	initialValue?: string | null
}
export function useRichTextArea({ label, extendedFormatting, isCompact = false, initialValue }: RichTextAreaParams) {
	const id = useId()
	const [editorRef, setEditorRef] = useState<LexicalEditorType>()
	const [isModified, setModified] = useState(false)

	const element = useMemo(
		() => (
			<Container>
				{label !== undefined && <InputLabel htmlFor={id}>{label}</InputLabel>}
				<RichTextArea id={id} $isCompact={isCompact}>
					<Suspense>
						<LexicalEditor editorRef={setEditorRef} extendedFormatting={extendedFormatting} />
					</Suspense>
				</RichTextArea>
			</Container>
		),
		[extendedFormatting, id, isCompact, label],
	)

	useLayoutEffect(() => {
		if (editorRef && initialValue !== undefined) {
			void loadLazyEditorState(editorRef, initialValue)
		}
	}, [initialValue, editorRef])

	useEffect(() => {
		if (!editorRef) return
		const removeListener = editorRef.registerUpdateListener(({ editorState }) => {
			if (initialValue) {
				setModified(JSON.stringify(editorState) !== initialValue)
			} else {
				void isLazyEditorStateEmpty(editorState).then(isEmpty => setModified(!isEmpty))
			}
		})
		return () => {
			removeListener()
		}
	}, [editorRef, initialValue])

	const handleClear = useCallback(() => {
		if (!editorRef) return
		void loadLazyEditorState(editorRef, null)
	}, [editorRef])

	const handleSetUnmodified = useCallback(() => {
		setModified(false)
	}, [])

	return useMemo(
		() => ({
			element,
			clear: handleClear,
			async exportEditorState() {
				if (!editorRef) return null
				return exportLazyEditorState(editorRef)
			},
			setUnmodified: handleSetUnmodified,
			isModified,
		}),
		[editorRef, element, handleClear, handleSetUnmodified, isModified],
	)
}

const Container = styled.div<{ $inline?: boolean }>`
	position: relative;
	min-width: 0;
	display: flex;
	flex-direction: column;
	${props =>
		props.$inline &&
		css`
			label {
				font-size: 11px;
				font-weight: 500;
				color: #78716c;
				text-transform: uppercase;
				letter-spacing: 0.12em;
				margin-bottom: 6px;
			}
		`}
`

const inputStyles = css<{ $isCompact: boolean; $inline?: boolean }>`
	width: 100%;
	font-size: 16px;
	color: black;
	background-color: white;
	border: ${props => (props.$inline ? "1px solid rgba(0,0,0,0.08)" : "1px solid var(--color-component-border)")};
	border-radius: ${props => (props.$inline ? "4px" : "8px")};
	transition: border-color 150ms, box-shadow 150ms;
	padding: ${props => (props.$isCompact ? "8px 12px" : props.$inline ? "12px 14px" : "16px")};

	&::placeholder {
		color: #6d6d6d;
	}

	&:focus {
		outline: none;
		${props =>
			props.$inline
				? css`
					border-color: rgba(120, 113, 108, 0.5);
					box-shadow: 0 0 0 2px rgba(120, 113, 108, 0.12);
				`
				: css`
					border-color: ${props => props.theme.primaryColor};
					box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}20;
				`}
	}

	&:disabled {
		background-color: ${componentBackgroundColor};
	}
`

const Input = styled.input<{ $isCompact: boolean; $inline?: boolean }>`
	${inputStyles}
	line-height: 1;
`

const TextArea = styled.textarea<{ $isCompact: boolean; $inline?: boolean }>`
	${inputStyles}
	resize: vertical;
	min-height: 104px;
`

const DynamicTextArea = styled(TextAreaAutoSize)<{ $isCompact: boolean; $inline?: boolean }>`
	${inputStyles}
	resize: none;
`

const RichTextArea = styled.div<{ $isCompact: boolean }>`
	${inputStyles}
	min-height: 104px; // Twice the height of an input field
	display: flex;
	flex-direction: column;
	cursor: text;

	h2 {
		font-size: 22px;
	}

	&:focus-within {
		outline: auto;
	}
`

const Counter = styled.div<{ $isAtLimit: boolean; $ratio: number }>`
	position: absolute;
	top: calc(100% + 4px);
	right: 0;
	pointer-events: none;
	text-align: right;
	font-size: 12px;
	line-height: 1;
	font-weight: 500;
	color: ${props => (props.$isAtLimit ? "#ef4444" : props.$ratio >= 0.75 ? "#d97706" : "#059669")};
`

export default InputField
